import { ChangeDetectorRef, Component, ElementRef, Inject, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { HttpClient } from '@angular/common/http';
import { SupervisionReviewReportItems, SupervisionReviewResponse, SupervisionReviewDocument, FlatResponseLog, GroupedResponseLog, ResponseOption, CreateSupervisionItemResponse } from '../../modules/models/supervision-model';
import { SUPERVISIONITEMSTATUS } from '../../modules/shared/common-enums';
import { SupervisionService } from '../../modules/services/supervision';
import { CommonDetailsService } from '../../modules/shared/common-details.service';
import { sanitizeTrustedHtml } from '../../modules/shared/ckeditor/utils/rich-text.utils';
import { forkJoin, Observable, of, Subject, EMPTY } from 'rxjs';
import { catchError, exhaustMap, switchMap, takeUntil, tap } from 'rxjs/operators';
import { saveAs } from 'file-saver';

type DrawerDisplayFieldType = 'clientName' | 'accountNumber' | 'departmentName';

interface DrawerDisplayRule {
  fieldType: DrawerDisplayFieldType;
  reportCodes: string[];
}

interface DrawerDisplayConfig {
  defaultFieldType: DrawerDisplayFieldType;
  rules: DrawerDisplayRule[];
}



@Component({
  selector: 'app-response-drawer',
  templateUrl: './response-drawer.component.html',
  styleUrls: ['./response-drawer.component.scss'],
  standalone: false
})
export class ResponseDrawerComponent implements OnInit {
  @ViewChild('responseLogScroll') responseLogScroll?: ElementRef<HTMLDivElement>;
  @ViewChildren('otherTextarea') otherTextareas?: QueryList<ElementRef<HTMLTextAreaElement>>;
  tradeBy = '';
  tradeByLabel = 'Account Number';
  referenceId = '';
  rowStatus = '';
  deferredExpirationDate = '';
  reportType = '';
  reportTypeValue = '';
  month = '';
  reportMonth = 0;
  reportYear = 0;
  reviewId = 0;
  supervisionItems: SupervisionReviewReportItems[] = [];
  flatResponseLogs: FlatResponseLog[] = [];
  groupedResponseLogs: GroupedResponseLog[] = [];
  otherResponseLogs: GroupedResponseLog[] = [];
  showAllLogs = false;
  attestationId = '';
  selectedFiles: File[] = [];
  fileDescriptions: string[] = [];
  showProgress = false;
  showDeferredOption = false;
  isClosedStatus = false;
  isDeferredStatus = false;
  isCONReportType = false;
  isPartyIdReport = false;
  responseOptions: ResponseOption[] = [];
  responseOptionsReady = false;
  today = new Date();
  maxDate = new Date();
  deferredDateError = false;
  fileSizeError = false;
  zeroFileSizeError = false;
  private submitTrigger$ = new Subject<void>();
  private destroy$ = new Subject<void>();
  private readonly MAX_UPLOAD_SIZE = 20 * 1024 * 1024; // 20 MB
  private readonly displayConfigPath = 'assets/config/response-drawer-display-config.json';

  private readonly fallbackDisplayConfig: DrawerDisplayConfig = {
    defaultFieldType: 'accountNumber',
    rules: [
      {
        fieldType: 'clientName',
        reportCodes: ['EPY', 'CON', 'DMA']
      },
      {
        fieldType: 'accountNumber',
        reportCodes: ['ALR', 'MFEX', 'LOIM', 'MFED', 'BETM', 'CLSC', 'FLIP', 'EREJ', 'ASM', 'REIN', 'BKPT', 'UCBR', 'MFFP', 'AIT', 'BET', 'BEX', 'BMC', 'CMS', 'ENOT', 'HBS', 'JRN', 'MMC', 'OSP', 'PNL', 'PSH', 'PST', 'SOL', 'TRN', 'LOIE', 'LOIW', 'MFBP', 'CONM']
      },
      {
        fieldType: 'departmentName',
        reportCodes: ['SUR']
      }
    ]
  };

  recordResponse = {
    status: 'Close',
    deferredChecked: false,
    deferredDate: null as Date | null,
    documentDesc: ''
  };

  constructor(
    public dialogRef: MatDialogRef<ResponseDrawerComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private supervisionService: SupervisionService,
    private httpClient: HttpClient,
    private sanitizer: DomSanitizer,
    private commonDetailsSvc: CommonDetailsService,
    private cdr: ChangeDetectorRef
  ) {
    this.tradeBy = data?.accountNumber || '';
    this.referenceId = data?.referenceId || this.tradeBy || '';
    this.rowStatus = this.normalizeStatus(data?.status);
    const rawDate = data?.row?.deferredExpirationDate || data?.row?.deferredexpirationdate || '';
    if (rawDate) {
      const datePart = typeof rawDate === 'string' && rawDate.length >= 10 ? rawDate.substring(0, 10) : rawDate;
      const d = new Date(datePart + 'T00:00:00');
      this.deferredExpirationDate = d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
      // If there's a deferredExpirationDate, the status is deferred - always set it
      this.recordResponse.deferredDate = d;
    }
    this.reportType = data?.reportType || '';
    if(this.reportType.toUpperCase() === 'CON') {
      this.isCONReportType = true;
    }
    this.reportTypeValue = data?.reportTypeValue || '';
    this.isPartyIdReport = ['DMA', 'CON'].includes(this.reportTypeValue.toUpperCase());
    this.month = data?.month || '';
    this.reportMonth = data?.reportMonth || 0;
    this.reportYear = data?.reportYear || 0;
    this.reviewId = data?.row?.reviewId ?? 0;
    this.attestationId = data?.row?.attestationID || '';
    this.supervisionItems = (data?.supervisionItems || []);
    const status = (data?.status ?? '').toString().toUpperCase();
    this.isClosedStatus = this.isStatusClosed(status);
    this.isDeferredStatus = this.isStatusDeferred(status);
    this.splitAndBuildLogs(this.supervisionItems);
    
    this.maxDate = this.commonDetailsSvc.calcdate(-365, new Date());
  }

  ngOnInit(): void {
    this.loadAndApplyDisplayConfig();
    this.registerSubmitHandler();
    this.loadResponseOptions();
  }

  private loadAndApplyDisplayConfig(): void {
    this.httpClient
      .get<DrawerDisplayConfig>(this.displayConfigPath)
      .pipe(
        catchError(() => of(this.fallbackDisplayConfig))
      )
      .subscribe((config) => {
        this.applyTradeByFromConfig(config || this.fallbackDisplayConfig);
      });
  }

  private applyTradeByFromConfig(config: DrawerDisplayConfig): void {
    const row = this.data?.row || {};
    const fieldType = this.resolveDisplayFieldType(config);
    this.tradeByLabel = this.getDisplayLabel(fieldType);

    const resolvedValue = this.resolveDisplayValue(row, fieldType);
    if (resolvedValue) {
      this.tradeBy = resolvedValue;
    } else {
      this.tradeBy = this.tradeBy || this.referenceId || '';
    }

    this.cdr.detectChanges();
  }

  private resolveDisplayFieldType(config: DrawerDisplayConfig): DrawerDisplayFieldType {
    const reportCodeCandidates = [this.reportType, this.reportTypeValue]
      .map(value => (value || '').toUpperCase().trim())
      .filter(Boolean);

    const rules = config?.rules || [];
    for (const rule of rules) {
      const ruleCodes = (rule.reportCodes || []).map(value => (value || '').toUpperCase().trim());
      if (reportCodeCandidates.some(code => ruleCodes.includes(code))) {
        return rule.fieldType;
      }
    }

    return config?.defaultFieldType || 'accountNumber';
  }

  private getDisplayLabel(fieldType: DrawerDisplayFieldType): string {
    if (fieldType === 'clientName') {
      return 'Client Name';
    }
    if (fieldType === 'departmentName') {
      return 'Department Name';
    }
    return 'Account Number';
  }

  private resolveDisplayValue(row: any, fieldType: DrawerDisplayFieldType): string {
    const readFirstNonEmpty = (keys: string[]): string => {
      for (const key of keys) {
        const value = row?.[key];
        if (value !== undefined && value !== null && String(value).trim()) {
          return String(value).trim();
        }
      }
      return '';
    };

    if (fieldType === 'clientName') {
      return readFirstNonEmpty(['clientName', 'client_name']);
    }

    if (fieldType === 'departmentName') {
      return readFirstNonEmpty(['TeamName', 'teamName', 'departmentName', 'department_name', 'departmentId']);
    }

    return readFirstNonEmpty(['accountNumber', 'account_number']);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.submitTrigger$.complete();
  }

  private registerSubmitHandler(): void {
    this.submitTrigger$.pipe(
      exhaustMap(() => this.buildSubmitPipeline()),
      takeUntil(this.destroy$)
    ).subscribe();
  }

  private buildSubmitPipeline(): Observable<SupervisionReviewReportItems[]> {
    const upload$ = this.selectedFiles.length
      ? forkJoin(this.selectedFiles.map(file => this.supervisionService.upload(file)))
      : of<string[]>([]);

    return upload$.pipe(
      switchMap((docIds) => this.saveResponse(docIds)),
      tap((response: CreateSupervisionItemResponse) => {
        this.reviewId = response.reviewId;
      }),
      switchMap(() => this.refreshResponseLogs()),
      catchError((err) => {
        console.error('Failed to submit supervision response', err);
        return EMPTY;
      })
    );
  }

  private isStatusClosed(status: any): boolean {
    const s = String(status ?? '').toUpperCase();
    return s === 'CLOSED' || s === String(SUPERVISIONITEMSTATUS.CLOSED);
  }

  private isStatusDeferred(status: any): boolean {
    const s = String(status ?? '').toUpperCase();
    return s === 'DEFERRED' || s === String(SUPERVISIONITEMSTATUS.DEFERRED);
  }

  private normalizeStatus(status: any): string {
    const s = String(status ?? '').trim();
    if (!s) return '';
    const upper = s.toUpperCase();
    if (this.isStatusClosed(upper)) return 'CLOSED';
    if (this.isStatusDeferred(upper)) return 'DEFERRED';
    if (upper === 'OPEN' || upper === String(SUPERVISIONITEMSTATUS.OPEN)) return 'OPEN';
    return upper;
  }

  private updateReadOnlyFromItems(items: SupervisionReviewReportItems[]): void {
    if (!items || !items.length) return;
    const mostRecent = [...items].sort(
      (a, b) => new Date(b.lastUpdatedDate || b.createdDate).getTime() - new Date(a.lastUpdatedDate || a.createdDate).getTime()
    )[0];
    this.rowStatus = this.normalizeStatus(mostRecent.status);
    this.isClosedStatus = this.isStatusClosed(mostRecent.status);
    this.isDeferredStatus = this.isStatusDeferred(mostRecent.status);
    if (this.isDeferredStatus && mostRecent.deferredExpirationDate) {
      const rawDate = mostRecent.deferredExpirationDate;
      const datePart = typeof rawDate === 'string' && rawDate.length >= 10 ? rawDate.substring(0, 10) : rawDate;
      const d = new Date(datePart + 'T00:00:00');
      this.deferredExpirationDate = isNaN(d.getTime()) ? rawDate : d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
      if (!isNaN(d.getTime())) {
        this.recordResponse.deferredDate = d;
      }
    } else if (!this.isDeferredStatus) {
      this.deferredExpirationDate = '';
      this.recordResponse.deferredDate = null;
    }
  }

  getCreatedDateEst(dateValue: string | undefined): string {
    return this.commonDetailsSvc.convertUTCToEST(dateValue || '');
  }

  getCreatedDateEstPart(dateValue: string | undefined, part: 'date' | 'time'): string {
    const formatted = this.commonDetailsSvc.convertUTCToEST(dateValue || '');
    const [datePart, timePart] = formatted.split(' | ');
    return part === 'date' ? (datePart || '') : (timePart || '');
  }

  private buildGroupedLogs(items: SupervisionReviewReportItems[]): void {
    const groupMap = new Map<string, GroupedResponseLog>();
    items.forEach((item: SupervisionReviewReportItems) => {
      // Group responses by each response's own createdDate + createdBy timestamp
      (item.responses || []).forEach((resp: SupervisionReviewResponse) => {
        const key = `${resp.createdDate}__${resp.createdBy}`;
        if (!groupMap.has(key)) {
          groupMap.set(key, {
            createdBy: resp.createdBy,
            createdDate: resp.createdDate,
            attestationId: item.attestationId || '',
            reportMonth: item.reportMonth,
            reportYear: item.reportYear,
            responses: [],
            documents: []
          });
        }
        if (!groupMap.get(key)!.reportMonth) {
          groupMap.get(key)!.reportMonth = item.reportMonth;
        }
        if (!groupMap.get(key)!.reportYear) {
          groupMap.get(key)!.reportYear = item.reportYear;
        }
        groupMap.get(key)!.responses.push(resp.responseDesc);
      });

      // Group each document by its own createdDate + createdBy timestamp.
      // If the document has no timestamp (API omits it), fall back to the LATEST
      // response in the same item — documents are always part of the most recent submission.
      const latestResp = (item.responses || []).reduce<SupervisionReviewResponse | null>(
        (latest, r) => !latest || new Date(r.createdDate) > new Date(latest.createdDate) ? r : latest,
        null
      );
      const fallbackDate = latestResp ? latestResp.createdDate : item.createdDate;
      const fallbackBy = latestResp ? latestResp.createdBy : item.createdBy;
      (item.documents || []).forEach((doc: SupervisionReviewDocument) => {
        const docCreatedDate = doc.createdDate || fallbackDate;
        const docCreatedBy = doc.createdBy || fallbackBy;
        const docKey = `${docCreatedDate}__${docCreatedBy}`;
        if (!groupMap.has(docKey)) {
          groupMap.set(docKey, {
            createdBy: docCreatedBy,
            createdDate: docCreatedDate,
            attestationId: item.attestationId || '',
            reportMonth: item.reportMonth,
            reportYear: item.reportYear,
            responses: [],
            documents: []
          });
        }
        if (!groupMap.get(docKey)!.reportMonth) {
          groupMap.get(docKey)!.reportMonth = item.reportMonth;
        }
        if (!groupMap.get(docKey)!.reportYear) {
          groupMap.get(docKey)!.reportYear = item.reportYear;
        }
        groupMap.get(docKey)!.documents.push(doc);
      });
    });
    this.groupedResponseLogs = Array.from(groupMap.values())
      .sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime());
  }

  private splitAndBuildLogs(items: SupervisionReviewReportItems[]): void {
    const currentId = (this.attestationId || '').toLowerCase();
    const matchingItems = items.filter(item => (item.attestationId || '').toLowerCase() === currentId);
    const otherItems = items.filter(item => (item.attestationId || '').toLowerCase() !== currentId);
    this.buildGroupedLogs(matchingItems);
    this.otherResponseLogs = this.buildGroupedLogsFrom(otherItems);
  }

  private buildGroupedLogsFrom(items: SupervisionReviewReportItems[]): GroupedResponseLog[] {
    const groupMap = new Map<string, GroupedResponseLog>();
    items.forEach((item: SupervisionReviewReportItems) => {
      (item.responses || []).forEach((resp: SupervisionReviewResponse) => {
        const key = `${resp.createdDate}__${resp.createdBy}__${item.reviewId}__${item.reportType}`;
        if (!groupMap.has(key)) {
          groupMap.set(key, { createdBy: resp.createdBy, createdDate: resp.createdDate, attestationId: item.attestationId || '', reportMonth: item.reportMonth, reportYear: item.reportYear, responses: [], documents: [] });
        }
        groupMap.get(key)!.responses.push(resp.responseDesc);
      });
      const latestResp = (item.responses || []).reduce<SupervisionReviewResponse | null>(
        (latest, r) => !latest || new Date(r.createdDate) > new Date(latest.createdDate) ? r : latest, null
      );
      const fallbackDate = latestResp ? latestResp.createdDate : item.createdDate;
      const fallbackBy = latestResp ? latestResp.createdBy : item.createdBy;
      (item.documents || []).forEach((doc: SupervisionReviewDocument) => {
        const docCreatedDate = doc.createdDate || fallbackDate;
        const docCreatedBy = doc.createdBy || fallbackBy;
        const docKey = `${docCreatedDate}__${docCreatedBy}__${item.reviewId}__${item.reportType}`;
        if (!groupMap.has(docKey)) {
          groupMap.set(docKey, { createdBy: docCreatedBy, createdDate: docCreatedDate, attestationId: item.attestationId || '', reportMonth: item.reportMonth, reportYear: item.reportYear, responses: [], documents: [] });
        }
        groupMap.get(docKey)!.documents.push(doc);
      });
    });
    return Array.from(groupMap.values())
      .sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime());
  }

  toggleShowMore(): void {
    this.showAllLogs = !this.showAllLogs;
  }

  getMonthName(monthNumber: number | undefined): string {
    return this.commonDetailsSvc.getMonthName(monthNumber);
  }

  getMonthNameFromDate(dateValue: string | undefined): string {
    if (!dateValue) {
      return 'N/A';
    }
    const d = new Date(dateValue);
    if (isNaN(d.getTime())) {
      return 'N/A';
    }
    return d.toLocaleDateString('en-US', { month: 'long' });
  }

  getYearFromDate(dateValue: string | undefined): string {
    if (!dateValue) {
      return 'N/A';
    }
    const d = new Date(dateValue);
    if (isNaN(d.getTime())) {
      return 'N/A';
    }
    return String(d.getFullYear());
  }

  private loadResponseOptions(): void {
    this.showProgress = true;
    this.responseOptionsReady = false;
    this.supervisionService.getRefSupervisionQuestionnaire(this.reportTypeValue).subscribe({
      next: (questionnaire) => {
        // Use next macrotask so bindings update after Angular's initial dev-mode check cycle.
        setTimeout(() => {
          this.showProgress = false;
          this.responseOptions = [];
          this.showDeferredOption = false;
          if (questionnaire && questionnaire.length) {
            this.responseOptions = questionnaire
              .filter(q => q.isActive === 'Y')
              .sort((a, b) => (a.uiOrder ?? 999) - (b.uiOrder ?? 999))
              .map(q => {
                const codeUpper = (q.responseCode || '').toUpperCase();
                const descUpper = (q.responseDesc || '').toUpperCase();
                const isOther = codeUpper.includes('OTHER') || codeUpper.includes('OTH')
                  || descUpper.includes('OTHER');
                return {
                  code: q.responseCode,
                  description: q.responseDesc,
                  checked: isOther,
                  isOther,
                  otherText: ''
                };
              });
            this.showDeferredOption = questionnaire.some(q => q.isActive === 'Y' && q.isDeferred === 'Y');
          }
          this.responseOptionsReady = true;
          this.cdr.detectChanges();
        }, 0);
      },
      error: (err) => {
        this.showProgress = false;
        this.responseOptionsReady = true;
        this.cdr.detectChanges();
        console.error('Error loading response options', err);
      }
    });
  }

  getInitials(email: string): string {
    if (!email) return '';
    const name = email.split('@')[0];
    const parts = name.split(/[._-]/);
    return parts.map(p => p.charAt(0).toUpperCase()).slice(0, 2).join('');
  }

  onOtherTextChanged(event: string | { html: string; editor: any }, opt: ResponseOption): void {
    opt.otherText = typeof event === 'string' ? event : (event?.html || '');
  }

  hasValidOtherText(opt: ResponseOption): boolean {
    return !!(opt.otherText && opt.otherText.trim().length > 0);
  }

  hasInvalidCheckedOtherResponse(): boolean {
    return false;
  }

  autoResize(textarea: HTMLTextAreaElement): void {
    textarea.style.height = 'auto';
    textarea.style.height = textarea.scrollHeight + 'px';
  }

  get canSubmit(): boolean {
    const hasResponse = this.responseOptions.some(opt => opt.checked && (!opt.isOther || this.hasValidOtherText(opt)));
    const hasDeferred = this.isCONReportType && this.showDeferredOption && this.recordResponse.deferredChecked && !!this.recordResponse.deferredDate;
    const hasDocuments = this.selectedFiles.length > 0;
    const hasInvalidOtherResponse = this.hasInvalidCheckedOtherResponse();
    
    // When status is OPEN and deferred checkbox is manually checked with a date, at least 1 response is required
    if (!this.isDeferredStatus && this.recordResponse.deferredChecked && this.recordResponse.deferredDate && !hasResponse) {
      return false;
    }
    
    return (hasResponse || hasDeferred || hasDocuments) && !hasInvalidOtherResponse;
  }

  isRichText(text: string): boolean {
    return /<[a-z][\s\S]*>/i.test(text || '');
  }

  isOtherResponseText(text: string): boolean {
    const normalizedText = (text || '').trim();
    if (!normalizedText) return false;
    return !this.responseOptions.some(opt => !opt.isOther && opt.description === normalizedText);
  }

  sanitizeHtml(html: string): SafeHtml {
    return sanitizeTrustedHtml(this.sanitizer, html || '');
  }

  private getTotalFileSize(extraFiles: File[] = []): number {
    return [...this.selectedFiles, ...extraFiles].reduce((sum, f) => sum + f.size, 0);
  }

  private hasZeroByteFile(files: File[]): boolean {
    return files.some(file => file.size === 0);
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      const newFiles = Array.from(input.files);
      if (this.hasZeroByteFile(newFiles)) {
        this.zeroFileSizeError = true;
        this.fileSizeError = false;
        input.value = '';
        return;
      }
      if (this.getTotalFileSize(newFiles) > this.MAX_UPLOAD_SIZE) {
        this.fileSizeError = true;
        this.zeroFileSizeError = false;
        input.value = '';
        return;
      }
      this.fileSizeError = false;
      this.zeroFileSizeError = false;
      this.selectedFiles.push(...newFiles);
      this.fileDescriptions.push(...newFiles.map(() => ''));
      input.value = '';
    }
  }

  onFileDrop(event: DragEvent): void {
    event.preventDefault();
    if (event.dataTransfer?.files?.length) {
      const newFiles = Array.from(event.dataTransfer.files);
      if (this.hasZeroByteFile(newFiles)) {
        this.zeroFileSizeError = true;
        this.fileSizeError = false;
        return;
      }
      if (this.getTotalFileSize(newFiles) > this.MAX_UPLOAD_SIZE) {
        this.fileSizeError = true;
        this.zeroFileSizeError = false;
        return;
      }
      this.fileSizeError = false;
      this.zeroFileSizeError = false;
      this.selectedFiles.push(...newFiles);
      this.fileDescriptions.push(...newFiles.map(() => ''));
    }
  }

  removeFile(index: number): void {
    this.selectedFiles.splice(index, 1);
    this.fileDescriptions.splice(index, 1);
    if (!this.selectedFiles.length) {
      this.zeroFileSizeError = false;
    }
    if (this.getTotalFileSize() <= this.MAX_UPLOAD_SIZE) {
      this.fileSizeError = false;
    }
  }

  submitResponse(): void {
    if (this.hasInvalidCheckedOtherResponse()) {
      return;
    }

    if (this.recordResponse.deferredChecked && !this.recordResponse.deferredDate) {
      this.deferredDateError = true;
      return;
    }

    this.deferredDateError = false;
    this.submitTrigger$.next();
  }

  private saveResponse(docIds: string[]): Observable<CreateSupervisionItemResponse> {
    const now = new Date().toISOString();
    const responses: SupervisionReviewResponse[] = this.responseOptions
      .filter(opt => opt.checked && (!opt.isOther || this.hasValidOtherText(opt)))
      .map(opt => ({
        responseCode: opt.code,
        responseDesc: opt.isOther ? (opt.otherText || '').trim() : opt.description,
        createdBy: '',
        createdDate: now
      }));

    const documents: SupervisionReviewDocument[] = this.selectedFiles.map((file, i) => ({
      documentId: docIds[i] || '',
      documentName: file.name,
      documentDesc: this.fileDescriptions[i] || ''
    }));

    const statusMap: Record<string, SUPERVISIONITEMSTATUS> = {
      CLOSED: SUPERVISIONITEMSTATUS.CLOSED,
      DEFERRED: SUPERVISIONITEMSTATUS.DEFERRED,
      OPEN: SUPERVISIONITEMSTATUS.OPEN
    };

    const hasResponse = responses.length > 0;

    // Logic:
    // 1. if the current status is OPEN and deferred checkbox is checked with a date → status is DEFERRED (atleast 1 response should be selected)
    // 2. Else if current status is DEFERRED and no responses (but can add documents only)→ status stays DEFERRED and Deferred Date is preserved both should be sent to Save API
    // 3. Else if current status is DEFERRED and has responses (and / or with documents uploaded)→ status becomes CLOSED
    // 4. Else → use default status (Close)
    const payloadStatus = this.recordResponse.deferredChecked && this.recordResponse.deferredDate
        ? SUPERVISIONITEMSTATUS.DEFERRED
        : this.isDeferredStatus && !hasResponse
          ? SUPERVISIONITEMSTATUS.DEFERRED
          : this.isDeferredStatus && hasResponse
            ? SUPERVISIONITEMSTATUS.CLOSED
            : statusMap[this.recordResponse.status] || SUPERVISIONITEMSTATUS.CLOSED;

    // Send deferred date if:
    // - User checked deferred checkbox with a date, OR
    // - Current status is deferred, no responses are checked, and deferred date exists
    const sendDeferredDate = (this.recordResponse.deferredChecked && this.recordResponse.deferredDate) 
        || (payloadStatus === SUPERVISIONITEMSTATUS.DEFERRED && !hasResponse && this.recordResponse.deferredDate);

    const isALRReportType = [this.reportTypeValue, this.reportType]
      .some(value => (value || '').toString().trim().toUpperCase() === 'ALR');
    const saveReferenceId = isALRReportType
      ? (this.data?.row?.attestationID || this.attestationId || this.referenceId || '')
      : (this.referenceId || '');

    const payload: SupervisionReviewReportItems = {
      reviewId: this.reviewId,
      attestationId: this.data?.row?.attestationID || '',
      reportType: this.data?.row?.reportType || this.reportType,
      reportMonth: this.reportMonth,
      reportYear: this.reportYear,
      status: payloadStatus,
      referenceId: saveReferenceId,
      deferredExpirationDate: sendDeferredDate
        ? this.recordResponse.deferredDate!.toISOString()
        : null,
      createdBy: this.data?.row?.createdBy || '',
      createdDate: this.data?.row?.createdDate || '',
      lastUpdatedBy: '',
      lastUpdatedDate: now,
      responses,
      documents
    };

    return this.supervisionService.saveSupervisionResponse(payload);
  }

  private refreshResponseLogs(): Observable<SupervisionReviewReportItems[]> {
    const request = {
      reviewId: 0,//this.reviewId,
      attestationId: '',
      reportType: this.reportTypeValue,
      reportMonth: 0,
      reportYear: 0,
      referenceId: this.referenceId || '',
      getStatusOnly: 'N'
    };

    return this.supervisionService.getSupervisionItems(request).pipe(
      tap((items) => {
        // Defer UI-bound mutations to avoid NG0100 during submit/update cycles.
        setTimeout(() => {
          this.supervisionItems = items || [];
          this.splitAndBuildLogs(this.supervisionItems);
          this.updateReadOnlyFromItems(this.supervisionItems);

          // Update displayed status in header
          if (this.isClosedStatus) {
            this.rowStatus = 'CLOSED';
          } else if (this.isDeferredStatus) {
            this.rowStatus = 'DEFERRED';
          }

        // Reset the record response form but preserve the deferred date
          const preservedDeferredDate = this.recordResponse.deferredDate;
          this.responseOptions.forEach(opt => { opt.checked = opt.isOther; opt.otherText = ''; });
          this.recordResponse = { status: 'Close', deferredChecked: false, deferredDate: preservedDeferredDate, documentDesc: '' };
          this.selectedFiles = [];
          this.fileDescriptions = [];
          this.zeroFileSizeError = false;
          this.cdr.detectChanges();
          // Collapse the auto-resized Other Response textarea back to its default height.
          this.otherTextareas?.forEach(ref => { ref.nativeElement.style.height = ''; });
        }, 0);
      })
    );
  }

  downloadDoc(doc: SupervisionReviewDocument): void {
    this.supervisionService.downloadfile(doc.documentId).subscribe(d => {
      saveAs(d, doc.documentName);
    });
  }

  close(): void {
    this.dialogRef.close();
  }

  resetResponseLogScroll(): void {
    const logContainer = this.responseLogScroll?.nativeElement;
    if (logContainer) {
      logContainer.scrollTop = 0;
    }
  }

}
